import { format as formatter, isValid, type Locale } from 'date-fns';
import { cs, de, enGB, fi, nb, sv } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import i18next from 'i18next';

import {
  type DatetimePreferences,
  getDatetimePreferences,
} from '../datetimePreferences/datetimePreferences';

const dateLocales: Record<string, Locale> = { en: enGB, nb, de, sv, fi, cz: cs };

function getDateLanguage() {
  const language = (i18next.language ?? 'en').toLowerCase().split('-')[0];
  return language === 'cs' ? 'cz' : language;
}

export function getDateLocale(): Locale {
  return dateLocales[getDateLanguage()] ?? enGB;
}

export function getDateInputFormat(): string {
  return getDatePatternWithYear(getDateLanguage(), getDatetimePreferences());
}

function getTimePattern(preferences: DatetimePreferences): string {
  return preferences.timeFormat === '12h' ? 'hh:mm a' : 'HH:mm';
}

function getDatePatternWithYear(langCode: string, preferences: DatetimePreferences): string {
  if (preferences.dateFormat === 'dmy-short') return 'dd.MM.yy';
  if (preferences.dateFormat === 'short-month') return 'd. MMM yy';
  if (preferences.dateFormat === 'locale') {
    return langCode === 'nb' ? 'd. MMMM yyyy' : 'd MMMM yyyy';
  }

  if (preferences.dateFormat === 'iso') {
    return 'yyyy-MM-dd';
  }

  if (preferences.dateFormat === 'mdy') {
    return 'MM/dd/yyyy';
  }

  return 'dd.MM.yyyy';
}

function getDatePatternWithoutYear(langCode: string, preferences: DatetimePreferences): string {
  if (preferences.dateFormat === 'short-month') return 'd. MMM';
  if (preferences.dateFormat === 'locale') {
    return langCode === 'nb' ? 'd. MMMM' : 'd MMMM';
  }

  if (preferences.dateFormat === 'iso') {
    return 'MM-dd';
  }

  if (preferences.dateFormat === 'mdy') {
    return 'MM/dd';
  }

  return 'dd.MM';
}

function getShortDatePatternWithYear(preferences: DatetimePreferences): string {
  if (preferences.dateFormat === 'dmy-short') return 'dd.MM.yy';
  if (preferences.dateFormat === 'short-month') return 'd. MMM yy';
  if (preferences.dateFormat === 'iso') {
    return 'yyyy-MM-dd';
  }

  if (preferences.dateFormat === 'mdy') {
    return 'MM/dd/yyyy';
  }

  return 'dd.MM.yyyy';
}

function getShortDatePattern(preferences: DatetimePreferences): string {
  if (preferences.dateFormat === 'short-month') return 'd. MMM';
  if (preferences.dateFormat === 'iso') {
    return 'MM-dd';
  }

  if (preferences.dateFormat === 'mdy') {
    return 'MM/dd';
  }

  return 'dd.MM';
}

function getTimeWithPrefix(langCode: string, preferences: DatetimePreferences): string {
  const timePattern = getTimePattern(preferences);
  return langCode === 'nb' ? `'kl.' ${timePattern}` : `'at' ${timePattern}`;
}

const datetimeFormats = {
  // 23:45
  time: (_langCode: string, preferences: DatetimePreferences) => getTimePattern(preferences),
  // kl. 23:45
  timeWithPrefix: (langCode: string, preferences: DatetimePreferences) =>
    getTimeWithPrefix(langCode, preferences),
  // mandag
  dayOfWeek: () => 'eeee',
  // uke 39
  week: (langCode: string) => (langCode === 'nb' ? "'uke' w" : "'week' w"),
  // september
  month: () => 'LLLL',
  // 2020
  year: () => 'yyyy',
  // september 2020
  monthWithYear: () => 'LLLL yyyy',
  // sep 2023
  monthShortWithYear: () => 'LLL yyyy',

  // 23. september kl. 23:45
  datetime: (langCode: string, preferences: DatetimePreferences) => {
    const timePattern = getTimePattern(preferences);
    const datePattern = getDatePatternWithoutYear(langCode, preferences);

    if (preferences.dateFormat === 'locale') {
      return langCode === 'nb' ? `d. MMMM 'kl.' ${timePattern}` : `d MMMM ${timePattern}`;
    }

    return langCode === 'nb'
      ? `${datePattern} 'kl.' ${timePattern}`
      : `${datePattern} ${timePattern}`;
  },
  // mandag 23. september kl. 23:45
  datetimeWithDay: (langCode: string, preferences: DatetimePreferences) => {
    const timePattern = getTimePattern(preferences);
    const datePattern = getDatePatternWithoutYear(langCode, preferences);

    if (preferences.dateFormat === 'locale') {
      return langCode === 'nb'
        ? `eeee d. MMMM 'kl.' ${timePattern}`
        : `eeee, d MMMM ${timePattern}`;
    }

    return langCode === 'nb'
      ? `eeee ${datePattern} 'kl.' ${timePattern}`
      : `eeee, ${datePattern} ${timePattern}`;
  },
  // 23. september 2020 kl. 23:45
  datetimeWithYear: (langCode: string, preferences: DatetimePreferences) => {
    const timePattern = getTimePattern(preferences);
    const datePattern = getDatePatternWithYear(langCode, preferences);

    if (preferences.dateFormat === 'locale') {
      return langCode === 'nb' ? `d. MMMM yyyy 'kl.' ${timePattern}` : `d MMMM yyyy ${timePattern}`;
    }

    return langCode === 'nb'
      ? `${datePattern} 'kl.' ${timePattern}`
      : `${datePattern} ${timePattern}`;
  },
  // 23.09.2020 kl. 23:45
  datetimeWithYearShort: (langCode: string, preferences: DatetimePreferences) => {
    const timePattern = getTimePattern(preferences);
    const datePattern = getShortDatePatternWithYear(preferences);

    return langCode === 'nb'
      ? `${datePattern} 'kl.' ${timePattern}`
      : `${datePattern} ${timePattern}`;
  },

  // 23. september
  date: (langCode: string, preferences: DatetimePreferences) => {
    if (preferences.dateFormat === 'locale') {
      return langCode === 'nb' ? 'd. MMMM' : 'd MMMM';
    }

    return getDatePatternWithoutYear(langCode, preferences);
  },
  // 23.09
  dateShort: (_langCode: string, preferences: DatetimePreferences) =>
    getShortDatePattern(preferences),
  // 23. september 2020
  dateWithYear: (langCode: string, preferences: DatetimePreferences) => {
    if (preferences.dateFormat === 'locale') {
      return langCode === 'nb' ? 'd. MMMM yyyy' : 'd MMMM yyyy';
    }

    return getDatePatternWithYear(langCode, preferences);
  },
  // 23.09.2020
  dateWithYearShort: (_langCode: string, preferences: DatetimePreferences) =>
    getShortDatePatternWithYear(preferences),
  // mandag 23. september
  dateWithDay: (langCode: string, preferences: DatetimePreferences) => {
    if (preferences.dateFormat === 'locale') {
      return langCode === 'nb' ? 'eeee d. MMMM' : 'eeee d MMMM';
    }

    return `eeee ${getDatePatternWithoutYear(langCode, preferences)}`;
  },
} as const;

export type DatetimeFormat = keyof typeof datetimeFormats;

export function formatDatetime(date: Date | string, format: DatetimeFormat): string {
  const datetime = new Date(date);
  if (!isValid(datetime)) {
    return '';
  }

  const preferences = getDatetimePreferences();
  const language = getDateLanguage();
  const dateFnsLocale = getDateLocale();
  const dateFnsFormat = datetimeFormats[format](language, preferences);

  return formatter(datetime, dateFnsFormat, {
    locale: dateFnsLocale,
    weekStartsOn: 1,
  });
}

export function formatDatetimeInTimezone(
  date: Date | string,
  format: DatetimeFormat,
  timezone?: string
): string {
  if (!timezone) {
    return formatDatetime(date, format);
  }

  const datetime = new Date(date);
  if (!isValid(datetime)) {
    return '';
  }

  const preferences = getDatetimePreferences();
  const language = getDateLanguage();
  const dateFnsLocale = getDateLocale();
  const dateFnsFormat = datetimeFormats[format](language, preferences);

  try {
    return formatInTimeZone(datetime, timezone, dateFnsFormat, {
      locale: dateFnsLocale,
      weekStartsOn: 1,
    });
  } catch {
    return formatDatetime(datetime, format);
  }
}
